import { Zap, Image as ImageIcon, Type, Palette, MessageSquare } from 'lucide-react';

/**
 * "Your Pipeline. Your Brand." — visual studio identity showcase card.
 *
 * Left: mockup of the Brand Dashboard settings with tier badges.
 * Right: three portal-preview frames (Starter / Pro / Ultimate) with tier visibility rules.
 */

const SETTINGS = [
    {
        icon: ImageIcon,
        label: 'Studio Logo Asset Upload',
        badge: 'Ultimate Only',
        badgeTone: 'ultimate',
    },
    {
        icon: Type,
        label: 'Custom Studio Brand Name Display',
        badge: 'Ultimate Only',
        badgeTone: 'ultimate',
    },
    {
        icon: Palette,
        label: 'Interface Accent Canvas Hex Color Override',
        badge: 'Pro & Ultimate',
        badgeTone: 'proUltimate',
    },
    {
        icon: MessageSquare,
        label: 'Custom Welcome Greeting Text',
        badge: 'Pro & Ultimate',
        badgeTone: 'proUltimate',
    },
];

const BADGE_STYLES = {
    ultimate: 'border-[#FF6B35]/50 text-[#FF6B35] bg-[#FF6B35]/5',
    proUltimate: 'border-amber-500/40 text-amber-500 bg-amber-500/5',
};

function SettingRow({ icon: Icon, label, badge, badgeTone }) {
    return (
        <div
            className="flex items-start gap-3 p-3 border border-border bg-background/60 hover:border-[#FF6B35]/40 transition-colors"
            data-testid={`brand-setting-${label.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}
        >
            <div className="h-8 w-8 grid place-items-center border border-border shrink-0">
                <Icon className="h-3.5 w-3.5 text-[#FF6B35]" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-tight">{label}</p>
                <span
                    className={`inline-block mt-1.5 text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 border ${BADGE_STYLES[badgeTone]}`}
                >
                    ✓ {badge}
                </span>
            </div>
        </div>
    );
}

function PortalPreview({ tier, tierBadge, logo, brandName, accentColor, greeting, note, showWatermark, testId }) {
    return (
        <div
            data-testid={testId}
            className="border border-border bg-background/70 overflow-hidden group"
        >
            {/* Preview header */}
            <div className="border-b border-border px-4 py-3 flex items-center justify-between bg-card/50">
                <div className="flex items-center gap-2">
                    {logo}
                    <p className="text-xs font-medium tracking-tight">{brandName}</p>
                </div>
                <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground border border-border px-1.5 py-0.5">
                    {tierBadge}
                </span>
            </div>
            {/* Preview body */}
            <div className="p-4 space-y-3">
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Portal — client view</p>
                <p className="text-sm font-medium leading-snug">{greeting}</p>
                <div
                    className="h-16 border border-dashed border-border grid place-items-center text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
                >
                    Drop-zone preview
                </div>
                <button
                    className="w-full py-2 text-[11px] uppercase tracking-[0.2em] text-white transition-colors"
                    style={{ backgroundColor: accentColor }}
                    tabIndex={-1}
                    type="button"
                >
                    Upload files
                </button>
                <p className="text-[10px] text-muted-foreground leading-snug border-l-2 border-[#FF6B35]/60 pl-2">
                    <span className="font-medium">{tier}:</span> {note}
                </p>
                {showWatermark && (
                    <p className="text-[9px] text-muted-foreground/70 italic text-center pt-1 border-t border-border">
                        Powered by Magnidrop
                    </p>
                )}
            </div>
        </div>
    );
}

export default function StudioBrandShowcase() {
    return (
        <section className="border-b border-border py-20" data-testid="studio-brand-showcase">
            <div className="mx-auto max-w-7xl px-6">
                <div className="max-w-3xl mb-10">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#FF6B35]">White-label preview</p>
                    <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-medium tracking-tighter">
                        Your Pipeline. Your Brand.
                    </h2>
                    <p className="mt-4 text-muted-foreground leading-relaxed">
                        Flip the studio branding controls on the left — watch the client portal on the right adapt in real-time. Tier visibility rules are baked in.
                    </p>
                </div>

                <div className="border border-border bg-card/60 backdrop-blur-sm p-6 lg:p-10 grid lg:grid-cols-12 gap-8">
                    {/* Left column: brand dashboard mockup */}
                    <div className="lg:col-span-5 space-y-4">
                        <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground border-l-2 border-[#FF6B35] pl-3">
                            Brand Dashboard · Settings
                        </p>
                        <div className="space-y-3">
                            {SETTINGS.map((s) => (
                                <SettingRow key={s.label} {...s} />
                            ))}
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed pt-2">
                            Configurable inside Dashboard → Settings → Branding. Tier gating enforced server-side.
                        </p>
                    </div>

                    {/* Right column: live client portal previews */}
                    <div className="lg:col-span-7 space-y-4">
                        <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground border-l-2 border-[#FF6B35] pl-3">
                            Live Client Portal · Result
                        </p>
                        <div className="grid sm:grid-cols-3 gap-4">
                            <PortalPreview
                                testId="preview-starter"
                                tier="Starter"
                                tierBadge="Starter"
                                logo={(
                                    <div className="h-6 w-6 grid place-items-center bg-[#FF6B35] text-white">
                                        <Zap className="h-3 w-3" strokeWidth={2.5} />
                                    </div>
                                )}
                                brandName="Magnidrop"
                                accentColor="#FF6B35"
                                greeting="Welcome to Magnidrop"
                                note="Standard Magnidrop typography, default sunset-orange buttons, and system greeting."
                                showWatermark={false}
                            />
                            <PortalPreview
                                testId="preview-pro"
                                tier="Pro"
                                tierBadge="Pro"
                                logo={(
                                    <div className="h-6 w-6 grid place-items-center bg-[#FF6B35] text-white">
                                        <Zap className="h-3 w-3" strokeWidth={2.5} />
                                    </div>
                                )}
                                brandName="Magnidrop"
                                accentColor="#0EA5A4"
                                greeting="Drop the raw footage — thanks, team!"
                                note="Buttons adopt your custom color; custom greeting shows. Default Magnidrop logo stays."
                                showWatermark={false}
                            />
                            <PortalPreview
                                testId="preview-ultimate"
                                tier="Ultimate"
                                tierBadge="Ultimate"
                                logo={(
                                    <div className="h-6 w-6 grid place-items-center bg-black text-white border border-border font-mono text-[10px]">
                                        LS
                                    </div>
                                )}
                                brandName="Lumen Studio"
                                accentColor="#A855F7"
                                greeting="Upload cuts to Lumen Studio."
                                note="Full white-label. Studio logo + brand name replace ours. Tiny watermark anchored bottom."
                                showWatermark={true}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
